import { useQuery } from '@tanstack/react-query';
import { ComponentProps, useEffect, useMemo } from 'react';

import { useSnackbar } from '@cms/providers/SnackbarProvider';
import { ALLOWED_LIMITS, FIXED_SEARCH_PARAMS } from '@cms/utils/constants';
import { generateSearchParam } from '@cms/utils/helpers';
import { generateQueryCacheKey, ISystemBase, ISystemResponse } from '@dl/declarations';
import { useSearchParams } from 'react-router-dom';
import api from '../../utils/api';
import { RenderModeType } from '../../utils/types';
import { Table } from '../data/Table';


type TableProps = Omit<ComponentProps<typeof Table>, "data" | "error" | "basePath">

type Props<TItem> = TableProps & {
    queryOptions: {
        basePath: string;
        enabled?: boolean;
        staleTime?: number;
        gcTime?: number;
        initialParams?: Record<string, string>;
        transformData?: (data: ISystemResponse<Array<TItem>>) => any
    }
    defaultSearchParams?: Record<string, string>;
}

export const DataPart = <TItem extends ISystemBase,>(props: Props<TItem>) => {
    const {
        pageSize = ALLOWED_LIMITS["10"],

        queryOptions = {
            basePath: '/',
            enabled: true,
            initialParams: {
                limit: pageSize
            },
        },

        defaultSearchParams = {},
        filters = {},

        ...restOfProps
    } = props;

    const { showSnackbar } = useSnackbar();
    const [searchParams, setSearchParams] = useSearchParams({
        [generateSearchParam(queryOptions.basePath, 'mode')]: 'view',
        [generateSearchParam(queryOptions.basePath, 'limit')]: String(queryOptions?.initialParams?.limit || pageSize),
        ...defaultSearchParams,
    });

    const mode = searchParams.get(generateSearchParam(queryOptions.basePath, "mode")) as RenderModeType;
    const should_refresh = searchParams.get(generateSearchParam(queryOptions.basePath, FIXED_SEARCH_PARAMS.SHOULD_REFRESH)) === 'true';

    const params = useMemo(() => {
        const paginationParams = [
            'sort',
            'limit',
            'skip',
        ]

        const allowedParams = [
            'query',
            ...Object.keys(filters)
        ]

        const usedSearchParams: URLSearchParams = new URLSearchParams()

        paginationParams.forEach((key) => {
            const value = searchParams.get(generateSearchParam(queryOptions.basePath, key))
            if (value) {
                usedSearchParams.set(key, value)
            }
        })

        allowedParams.forEach((key) => {
            const values = searchParams.getAll(generateSearchParam(queryOptions.basePath, key))

            if (
                values &&
                values.length > 0 &&
                (values.findIndex((v) => v === 'all') === -1)
            ) {
                usedSearchParams.set(key, values.join(','))
            }
            else if (filters[key]?.defaultValue && filters[key]?.defaultValue !== 'all') {
                usedSearchParams.set(key, String(filters[key].defaultValue))
            }
        })

        const newParams = new URLSearchParams({
            ...(queryOptions.initialParams),
            ...(Object.fromEntries(usedSearchParams.entries()))
        })

        return newParams;
    }, [queryOptions.initialParams, searchParams, filters])

    const { data, isLoading, isFetching, error, refetch } = useQuery<ISystemResponse<Array<TItem>>>({
        queryKey: [`${queryOptions.basePath}-by-search`, queryOptions.basePath, generateQueryCacheKey(Object.fromEntries(params.entries()))],
        queryFn: async () => {
            const res = await api.get(
                queryOptions.basePath, {
                params
            })

            return res.data
        },

        gcTime: queryOptions.gcTime ?? (1000 * 60 * 10),
        staleTime: queryOptions.staleTime ?? (1000 * 60 * 5),

        select: queryOptions.transformData,
        enabled: queryOptions.enabled && mode === 'view',

        placeholderData: (previousData) => previousData,
    })

    useEffect(() => {
        if (should_refresh) {
            refetch()
                .finally(() => {
                    setSearchParams((prev) => {
                        prev.delete(generateSearchParam(queryOptions.basePath, 'should_refresh'))
                        return prev
                    })
                })
        }
    }, [should_refresh, refetch])

    useEffect(() => {
        if (error) {
            showSnackbar("Failed to load data. " + (error as Error).message || 'Unknown error', 'error');
        }
    }, [error])

    /** 
     * This logic is commented out to preserve search params 
     * when navigating away from the table view to detail/edit views.
    */
    // useUnmount(() => {
    //     setSearchParams((prev) => {
    //         const allParams = Array.from(prev.entries())

    //         allParams.forEach(([key, _value]) => {
    //             if (
    //                 key.startsWith(generateSearchParam(queryOptions.basePath, ''))
    //                 &&
    //                 key !== generateSearchParam(queryOptions.basePath, 'id')
    //             ) {
    //                 prev.delete(key)
    //             }
    //         })

    //         return prev
    //     })
    // })

    return (
        <Table<TItem>
            {...restOfProps}
            filters={filters}
            pageSize={pageSize}
            data={data || {
                data: [],
                count: 0,
                total_count: 0,
                next_skip: 0,
            }}
            error={error}
            isLoading={isFetching || isLoading}
            basePath={queryOptions.basePath}
            defaultSearchParams={defaultSearchParams}
        />
    )
}